import { useState, useEffect, useContext } from "react";
import WeightEstimation from "components/weight-estimation";
import RadioGroup from "components/radio-group";
import FormContainer from "components/form-container";
import {
  checkIfFullKiosk,
  getQueryParams,
  localizeDecimalSeparator,
} from "lib/utils";
import { calculateEstimate } from "lib/calculateEstimate";
import { useOutletContext, useLocation } from "react-router-dom";
import ProgressBar from "components/progress-bar";
import { useRestartIfAbsent } from "hooks/useRestartIfAbsent";
import { useCurrentLocation } from "lib/utils";
import { useTranslation } from "react-i18next";
import {
  TAKEBACK_METHOD_NAME,
  TAKEBACK_REPLAY_WEIGHT_NAME,
} from "consts/query-params";
import useWhen from "hooks/useWhen";
import { PAYMENT_ARGS } from "consts/feature-config";
import AuthContext from "contexts/AuthContext";
import ReplayLogo from "assets/replaylogo.png";
import WeightInput from "components/weight-input";
import DonationWeightChart from "components/donation-weight-chart";
import InputValidation from "components/input-validation";
import Modal from "components/modal";
import CloseIcon from "assets/icons/close.svg";
import { AnimatePresence } from "framer-motion";
import FloodlightEvent from "components/floodlight-event";
import { EVENT_9, EVENT_32 } from "utils/tracking";

export default function DonationPage() {
  // translation
  const { t } = useTranslation();

  // Get query params and restart flow if absent
  const location = useLocation();
  const queryParams = getQueryParams(location.search);
  useRestartIfAbsent(queryParams, ["w"], t("urls.trade_in_weight"));

  // Check if we're on the adapt route
  const isAdaptRoute = location.pathname === t("urls.trade_in_donation_adapt");

  // Calculate total weight first
  const totalWeight = parseFloat(queryParams.w);
  const maxDonationWeight = 100; // Now it's percentage

  // Initialise input with query param
  const [selectedOption, setSelectedOption] = useState(
    (isAdaptRoute && queryParams[TAKEBACK_REPLAY_WEIGHT_NAME]) ||
      (queryParams.replayPercentage &&
        parseFloat(queryParams.replayPercentage) !== 0)
      ? "donation"
      : null
  );

  const handleDonationChange = (choice) => {
    if (selectedOption === null && choice === `donation`) {
      console.log(`Tracked: "Trade-in donation chosen" as "event15"`);
      if (window.s) {
        window.s.events = `event15`;
        window.s.linkTrackEvents = `event15`;
        window.s.linkTrackVars = "events";
        window.s.tl(null, "o", "Trade-in donation chosen");
      }
    }
    setSelectedOption(choice);
  };

  const [donationWeight, setDonationWeight] = useState(
    isAdaptRoute && queryParams[TAKEBACK_REPLAY_WEIGHT_NAME]
      ? Math.round(
          (parseFloat(queryParams[TAKEBACK_REPLAY_WEIGHT_NAME]) / totalWeight) *
            100
        ).toString()
      : queryParams.replayPercentage &&
          parseFloat(queryParams.replayPercentage) !== 0
        ? queryParams.replayPercentage.toString()
        : ""
  );
  const [warningMessage, setWarningMessage] = useState("");
  const [replayModalOpen, setReplayModalOpen] = useState(false);

  // Handle donation weight change
  const handleDonationWeightChange = (event) => {
    let newValue = event.target.value.replace(/[^0-9]/g, ""); // Only allow digits
    newValue = newValue.slice(0, 3); // Limit to 3 digits
    setDonationWeight(newValue);

    const newPercentage = parseInt(newValue);
    if (!isNaN(newPercentage) && newPercentage > maxDonationWeight) {
      setWarningMessage(
        `You cannot donate more than 100% of your total weight.`
      );
    } else {
      setWarningMessage("");
    }
  };

  // Calculate remaining weight based on percentage
  const donationPercentage = parseInt(donationWeight) || 0;
  const actualDonationWeight = totalWeight * (donationPercentage / 100);
  const remainingWeight = (totalWeight - actualDonationWeight).toFixed(2);

  // Generate updated params
  const updatedParams =
    selectedOption === "no_donation"
      ? (() => {
          const { replayPercentage, ...params } = queryParams;
          return params;
        })()
      : {
          ...queryParams,
          // ...(selectedOption === "donation" &&
          //   parseFloat(donationWeight) > 0 && {
          //     [TAKEBACK_REPLAY_WEIGHT_NAME]: actualDonationWeight,
          //   }),
          ...(selectedOption === "donation" &&
            parseFloat(donationWeight) > 0 && {
              replayPercentage: donationWeight,
            }),
        };

  const { location: currentLocation } = useCurrentLocation();

  const { user } = useContext(AuthContext);

  //reset donation weight when no donation is selected
  useEffect(() => {
    if (selectedOption === "no_donation") {
      setDonationWeight("");
    }
  }, [selectedOption]);

  // Radio options
  const radioOptions = [
    {
      value: "donation",
      title: "Yes, donate a portion of my LEGO bricks.",
      subtitle: "Any donated bricks will not earn a reward.",
    },
    {
      value: "no_donation",
      title: "No, receive the full trade-in value of my LEGO bricks.",
      subtitle: "Once verified, you'll be sent a LEGO e-Gift Card.",
    },
  ];

  const estimate = calculateEstimate(remainingWeight.toString());

  const parsedWeight = parseFloat(queryParams.w).toFixed(2);
  const weightEstimate =
    currentLocation?.locale !== "en-US"
      ? parsedWeight.toString().replace(".", ",")
      : parsedWeight;

  // update right side visual
  const { setSidebarVisual } = useOutletContext();

  useEffect(() => {
    setSidebarVisual("replay");
  }, [setSidebarVisual]);

  //only show cash payment in appropriate environments
  const showPayment = useWhen(PAYMENT_ARGS);

  const isKiosk = checkIfFullKiosk(currentLocation);

  return (
    <FormContainer
      submitLabel={isAdaptRoute ? "Save Changes" : t("ui.button_continue")}
      submitHref={`${isAdaptRoute ? t("urls.trade_in_summary") : !user ? t("urls.trade_in_email") : t("urls.trade_in_terms")}`}
      submitParams={updatedParams}
      submitDisabled={
        !selectedOption ||
        (selectedOption === "donation" && !donationWeight) ||
        parseFloat(donationWeight) > 100 ||
        (selectedOption === "donation" && parseFloat(donationWeight) === 0)
      }
      submitTracking={
        selectedOption === `donation` ? [EVENT_9, EVENT_32] : [EVENT_9]
      }
      secondaryLabel={t("ui.button_back")}
      secondaryHref={`${isAdaptRoute ? t("urls.trade_in_summary") : t("urls.trade_in_checklist")}`}
      secondaryParams={queryParams}
    >
      <FloodlightEvent tag="DC-14832541/pageview/pl_donat+per_session" />
      <ProgressBar
        step={currentLocation?.state === null ? 3 : 4}
        showLabel
        stepsNum={
          currentLocation?.state === null
            ? user
              ? 5
              : 6
            : queryParams[TAKEBACK_METHOD_NAME] === "mail"
              ? user
                ? 6
                : 7
              : isKiosk
                ? user
                  ? 5
                  : 6
                : user
                  ? 4
                  : 5
        }
      />
      <div className="flex flex-col space-y-regular">
        <img src={ReplayLogo} alt="Replay Logo" className="w-[80px] mb-2" />
        <h2 className="text-primary text-[29px] font-bold leading-[32px]">
          Optional Donation to LEGO® Replay
        </h2>
        <p className="text-tertiary text-[18px] leading-[23px]">
          Would you like to donate a portion of your bricks to LEGO Replay? LEGO
          Replay gives pre-loved bricks to kids in need of play across the US.{" "}
          <span
            className="text-[#006cb7] cursor-pointer"
            onClick={() => setReplayModalOpen(true)}
            tabIndex={0}
            role="button"
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setReplayModalOpen(true);
            }}
          >
            Learn more.
          </span>
        </p>
      </div>
      <div>
        <h3 className="text-[18px] font-bold leading-[23px]">
          Would you like to donate bricks to LEGO Replay?
        </h3>
        <RadioGroup
          options={radioOptions}
          name="custom-radio"
          selectedOption={selectedOption}
          onChange={handleDonationChange}
          className="mt-2"
        />
      </div>
      {selectedOption === "donation" && (
        <div className="flex flex-col space-y-regular">
          <p className="text-[16px] leading-[21px]">
            Enter the percentage of verified LEGO bricks and elements you'd like
            to donate*
          </p>
          <div className="flex items-center gap-large justify-between w-full">
            <WeightInput
              hideCaption
              placeholder="0"
              unit="%"
              className="max-w-[100px]"
              value={donationWeight}
              onChange={handleDonationWeightChange}
              locale="en-US"
            />
            {parseFloat(donationWeight) > 0 && donationWeight <= 100 && (
              <div className="flex flex-grow items-center justify-between bg-gray-300/25 px-6 h-[50px] rounded-lg">
                <div className="font-bold">
                  {actualDonationWeight.toFixed(2)} lbs
                </div>
                <div className="w-[1px] h-[35px] bg-gray-300"></div>
                <div className="font-bold text-[#4C2F92] text-sm">
                  Thank you for choosing to donate to LEGO Replay.
                </div>
              </div>
            )}
          </div>
          <p className="text-[#757575] text-[12px] leading-[16px]">
            *Any donated bricks will not earn a reward.
          </p>
          {warningMessage && <InputValidation errorMessage={warningMessage} />}
          {parseFloat(donationWeight) > 0 &&
            parseFloat(donationWeight) <= 100 && (
              <div className="pt-xx-large flex flex-col space-y-regular">
                <h3 className="text-[18px] font-bold leading-[23px]">
                  Your estimated trade-in breakdown
                </h3>
                <p className="text-sm text-secondary pb-4">
                  The final reward and donation values will be based on the
                  verified weight of eligible LEGO bricks and elements.
                </p>
                <DonationWeightChart
                  totalWeight={queryParams.w}
                  replayWeight={actualDonationWeight}
                  showBackground
                  rewardCaption="Reward"
                  showTotalEstimatedWeight={true}
                  replayPercentage={donationWeight}
                />
              </div>
            )}
        </div>
      )}
      {!parseFloat(donationWeight) && (
        <WeightEstimation
          title="YOUR REWARD WILL BE WORTH"
          visual="giftcard"
          singleValue={
            !showPayment
              ? localizeDecimalSeparator(estimate.gift, currentLocation)
              : null
          }
          range={
            showPayment
              ? [
                  localizeDecimalSeparator(estimate.payment, currentLocation),
                  localizeDecimalSeparator(estimate.gift, currentLocation),
                  currentLocation,
                ]
              : null
          }
          animate
          locale={currentLocation?.locale}
          description="Paid out as a LEGO® e-Gift Card"
          additionalDescription={`Estimated weight: ${weightEstimate} lbs`}
        />
      )}
      <AnimatePresence>
        {replayModalOpen && (
          <Modal setModalOpen={setReplayModalOpen} modalOpen={replayModalOpen}>
            <div className="flex flex-col gap-large py-4">
              <div className="flex justify-between mb-4">
                <h2 className="text-4xl font-semibold leading-none text-left">
                  What is LEGO Replay?
                </h2>
                <div
                  className="cursor-pointer"
                  onClick={() => setReplayModalOpen(false)}
                >
                  <img src={CloseIcon} alt="Close" />
                </div>
              </div>
              <div>
                <p className="text-base text-tertiary text-left">
                  LEGO Replay is a sustainable way to keep as many bricks in
                  play as possible and share the joy of LEGO play with kids who
                  might not have access. Donated bricks are sorted, cleaned, and
                  packed into Replay boxes that are distributed via The LEGO
                  Group's trusted community partners to impact more children.
                </p>
              </div>
            </div>
          </Modal>
        )}
      </AnimatePresence>
    </FormContainer>
  );
}
